/** The household feature flags: keys, display labels, and a type guard. Shared by server and client. */

import type { FlagKey, Flags } from "./types";

export const FLAG_KEYS: readonly FlagKey[] = ["bountyUI", "notesUI", "economyUI", "focusUI", "quickUI", "originalAssets", "livePeon", "upgradesUI", "passkeys", "standings", "recapUI", "timersUI", "shopUI", "achievementsUI", "ranksUI", "purseUI", "claimUI", "nudgeUI", "weeklyUI", "wallpaperUI", "streakUI", "rushPickUI", "auctionUI", "wagerUI", "galleryUI", "compareUI", "peonMoodsUI", "rouletteUI", "yoursUI", "giveCreditUI", "trinketsUI", "lateRushUI", "heartsUI", "mentionsUI", "speedrunUI", "photoNotesUI", "slopTallyUI", "feedbackUI", "yoursPushUI", "cheersUI", "helloUI", "houseGoalUI", "themeWeekUI", "shareCreditUI", "projectsUI"];

/** Display label for the DevSheet's Features block. */
export const FLAG_LABELS: Record<FlagKey, string> = {
  bountyUI: "Bounties",
  notesUI: "Pinned notes",
  economyUI: "Task economy",
  focusUI: "Next up",
  quickUI: "Quick bonus",
  originalAssets: "Original art and voice",
  livePeon: "Animated peon",
  upgradesUI: "Dynasty upgrades",
  passkeys: "Passkeys and passwords",
  standings: "Monthly standings",
  recapUI: "Season recap",
  timersUI: "Timed notes",
  shopUI: "Peon shop",
  achievementsUI: "Achievements",
  ranksUI: "Ranks",
  purseUI: "Purse",
  claimUI: "Me do it",
  nudgeUI: "Nudges",
  weeklyUI: "Weekly scroll",
  wallpaperUI: "Household wallpaper",
  streakUI: "Streaks",
  rushPickUI: "Gold rush pick",
  auctionUI: "Luxury auction",
  wagerUI: "Wagers",
  galleryUI: "Done gallery",
  compareUI: "Photo compare",
  peonMoodsUI: "Peon moods",
  rouletteUI: "Task roulette",
  yoursUI: "Yours",
  giveCreditUI: "Give credit",
  trinketsUI: "Trinkets",
  lateRushUI: "Late chore rush",
  heartsUI: "Note hearts",
  mentionsUI: "Name tags",
  speedrunUI: "Speedrun note",
  photoNotesUI: "Photo notes",
  slopTallyUI: "Slop tally",
  feedbackUI: "Suggest something",
  yoursPushUI: "Yours reminder",
  cheersUI: "Cheers",
  helloUI: "Hello by name",
  houseGoalUI: "House goal",
  themeWeekUI: "Themed weeks",
  shareCreditUI: "Shared credit",
  projectsUI: "Projects",
};

/** Every flag off: a test's starting point, spread and overridden (`{ ...FLAGS_OFF, focusUI: true }`). */
export const FLAGS_OFF: Flags = Object.fromEntries(FLAG_KEYS.map((k) => [k, false])) as Flags;

/**
 * Roadmap flags registered ahead of their feature (docs/roadmap-2026-10.md): the workshop leaves these
 * rows out until the feature ships, so a switch never sits there doing nothing (owner, 2026-10-05,
 * on the compare slider's switch before the slider existed: "Works on some not on others"). A flag
 * leaves this set in the same change that ships its feature.
 */
export const UNBUILT_FLAGS: ReadonlySet<FlagKey> = new Set<FlagKey>([]);

/** The flags the workshop lists: every key with a feature behind it, in FLAG_KEYS order. */
export function workshopFlags(): FlagKey[] {
  return FLAG_KEYS.filter((k) => !UNBUILT_FLAGS.has(k));
}

/**
 * The flags a new household starts with. First every flag that existed on 2026-10-03 (owner,
 * docs/product.md "Flags (2026-10-03)": "All current feature flags should be enabled by default in
 * new households", then "I don't want future flags to be covered too"); since 2026-10-07 every flag
 * the owner's own household had on that day (owner: "Add all of the currently enabled flags on
 * Omie's household to the defaults"), which is every flag but wallpaperUI, rushPickUI, wagerUI and
 * peonMoodsUI. The list is frozen on purpose and is NOT FLAG_KEYS: a flag added later is off for new
 * households until someone adds it here deliberately. server/households.ts newHouseholdFlags writes
 * it; the workshop calls these the old flags (workshopFlagGroups).
 */
export const NEW_HOUSEHOLD_FLAGS_2026_10_07: readonly FlagKey[] = [
  "bountyUI",
  "notesUI",
  "economyUI",
  "focusUI",
  "quickUI",
  "originalAssets",
  "livePeon",
  "upgradesUI",
  "passkeys",
  "standings",
  "recapUI",
  "timersUI",
  "shopUI",
  "achievementsUI",
  "ranksUI",
  "purseUI",
  "claimUI",
  "nudgeUI",
  "weeklyUI",
  "streakUI",
  "auctionUI",
  "galleryUI",
  "compareUI",
  "rouletteUI",
  "yoursUI",
];

/**
 * The workshop's Features rows split in two (owner, 2026-10-05: "All of the default feature flags
 * should be put behind a show old flags text that reveals them"): `current`, the flags off by default,
 * always shown; `old`, the flags a new household starts with on, hidden behind "Show old flags". Each
 * keeps workshopFlags order.
 */
export function workshopFlagGroups(): { current: FlagKey[]; old: FlagKey[] } {
  const all = workshopFlags();
  return {
    current: all.filter((k) => !NEW_HOUSEHOLD_FLAGS_2026_10_07.includes(k)),
    old: all.filter((k) => NEW_HOUSEHOLD_FLAGS_2026_10_07.includes(k)),
  };
}

export function isFlagKey(x: unknown): x is FlagKey {
  return typeof x === "string" && (FLAG_KEYS as readonly string[]).includes(x);
}

// ---------- Device preview (docs/product.md "Season recap") ----------

/**
 * A device whose own switch is on for a previewable flag says so on every API request in this
 * header, a comma-separated list of FlagKeys ("recapUI"), so the server can show that device alone
 * what the household switch would show everyone. Only a signed-in household session reaches a route
 * that reads it, so it opens nothing to anyone outside the household.
 */
export const FLAG_PREVIEW_HEADER = "x-flag-preview";

/**
 * The flags a device switch may preview server-side: the season recap, photo notes (a device
 * trying them with its own switch may pin a photo while the household's is off; docs/notes.md
 * "Photo notes"), the slop tally (a device trying it starts the household's count on its first
 * board read, as the household's switch would; docs/notes.md "The slop tally"), and the house goal (a
 * device trying it starts the week's goal on its board read; docs/house-week.md).
 */
export const PREVIEW_FLAGS: readonly FlagKey[] = ["recapUI", "photoNotesUI", "slopTallyUI", "houseGoalUI"];

/** The header's FlagKeys, trimmed, deduplicated, anything that is not a FlagKey dropped. */
export function parseFlagPreview(raw: string | null | undefined): FlagKey[] {
  if (!raw) return [];
  const out: FlagKey[] = [];
  for (const part of raw.split(",")) {
    const key = part.trim();
    if (isFlagKey(key) && !out.includes(key)) out.push(key);
  }
  return out;
}

/** The header's value for the previewable flags this device has switched on, or null for none. */
export function flagPreviewValue(localOn: (key: FlagKey) => boolean): string | null {
  const on = PREVIEW_FLAGS.filter((k) => localOn(k));
  return on.length > 0 ? on.join(",") : null;
}
